/**
 * Supabase Storage image-transform helper.
 *
 * Rewrites public-object URLs to the `/render/image/` endpoint so the CDN
 * returns a resized, format-negotiated (AVIF/WebP) variant. Falls back to
 * the original URL for any non-Supabase host so external/Unsplash images
 * keep working.
 *
 * Requires Supabase Image Transformations to be enabled on the project
 * (Pro plan and above). If the feature is disabled the render endpoint
 * 404s — in that case set `IMAGE_TRANSFORMS_ENABLED` to false below.
 */

const IMAGE_TRANSFORMS_ENABLED = true;
const PUBLIC_SEGMENT = '/storage/v1/object/public/';
const RENDER_SEGMENT = '/storage/v1/render/image/public/';

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number; // 20-100, default 75
  resize?: 'cover' | 'contain' | 'fill';
}

export function transformImageUrl(url: string | null | undefined, opts: ImageTransformOptions = {}): string {
  if (!url) return '';
  if (!IMAGE_TRANSFORMS_ENABLED) return url;
  // Only transform Supabase public-storage URLs.
  if (!url.includes(PUBLIC_SEGMENT)) return url;

  const rendered = url.replace(PUBLIC_SEGMENT, RENDER_SEGMENT);
  const params = new URLSearchParams();
  if (opts.width) params.set('width', String(opts.width));
  if (opts.height) params.set('height', String(opts.height));
  params.set('quality', String(opts.quality ?? 75));
  if (opts.resize) params.set('resize', opts.resize);
  return `${rendered}?${params.toString()}`;
}

/**
 * Build a responsive srcSet string for the common breakpoints.
 */
export function buildSrcSet(url: string | null | undefined, widths: number[] = [400, 800, 1200, 1600]): string {
  if (!url || !url.includes(PUBLIC_SEGMENT) || !IMAGE_TRANSFORMS_ENABLED) return '';
  return widths
    .map((w) => `${transformImageUrl(url, { width: w })} ${w}w`)
    .join(', ');
}

/**
 * onError handler that falls back to the original (untransformed) URL when
 * the Supabase render endpoint returns an error for an individual asset
 * (e.g. too-large file, unsupported source format, transient outage).
 *
 * Usage:
 *   <img src={transformImageUrl(url, { width: 800 })}
 *        srcSet={buildSrcSet(url)}
 *        onError={(e) => fallbackToOrigin(e, url)} />
 */
export function fallbackToOrigin(
  e: React.SyntheticEvent<HTMLImageElement>,
  originalUrl: string | null | undefined,
): void {
  const img = e.currentTarget;
  if (!originalUrl || img.dataset.fallback === '1') return;
  img.dataset.fallback = '1';
  // Clearing srcset prevents the browser from re-selecting another failing
  // transformed variant. Then point src at the original public URL.
  img.srcset = '';
  img.src = originalUrl;
}